import { SERVER_BASE_URL } from '@telesoft/lib/web/constants'
import { Button, ButtonProps } from '../../components/ui/button'
import { Career, Category, Page, Post, Resource } from '@telesoft/types/payload-types'
import Link from 'next/link'
import React from 'react'

export type LinkFieldType = {
  type?: 'reference' | 'custom'
  newTab?: boolean
  reference:
    | {
        value: string | Page
        relationTo: 'pages'
      }
    | {
        value: string | Post
        relationTo: 'posts'
      }
    | {
        value: string | Resource
        relationTo: 'resources'
      }
    | {
        value: string | Category
        relationTo: 'categories'
      }
    | {
        value: string | Career
        relationTo: 'careers'
      }
  url: string
  label: string
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive'
}

type PageReference = {
  value: string | Page
  relationTo: 'pages'
}

type PostReference = {
  value: string | Post
  relationTo: 'posts'
}

type CategoryReference = {
  value: string | Category
  relationTo: 'categories'
}

type ResourceReference = {
  value: string | Resource
  relationTo: 'resources'
}

type CareerReference = {
  value: string | Career
  relationTo: 'careers'
}

export type LinkType = 'reference' | 'custom'
export type Reference =
  | PageReference
  | PostReference
  | CategoryReference
  | ResourceReference
  | CareerReference

export type CMSLinkType = {
  type?: LinkType
  newTab?: boolean
  reference?: Reference
  url?: string
  label?: string
  showLabel?: boolean
  inverted?: ButtonProps['inverted']
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  children?: React.ReactNode
  className?: string
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  buttonProps?: ButtonProps
}

type GenerateSlugType = {
  type?: LinkType
  url?: string
  reference?: Reference
}

const generateHref = (args: GenerateSlugType): string => {
  const { reference, url, type } = args

  if ((type === 'custom' || type === undefined) && url) {
    return url
  }

  if (type === 'reference' && reference?.value && typeof reference.value !== 'string') {
    if (reference.relationTo === 'pages') {
      const value = reference.value as Page
      const breadcrumbs = value?.breadcrumbs
      const hasBreadcrumbs = breadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length > 0
      if (hasBreadcrumbs) {
        return breadcrumbs[breadcrumbs.length - 1]?.url as string
      }
    }

    if (reference.relationTo === 'posts') {
      return `/blog/${reference.value.slug}`
    }

    if (reference.relationTo === 'categories') {
      return `/content/${reference.value.slug}`
    }

    if (reference.relationTo === 'resources') {
      return `${SERVER_BASE_URL}/resources/${reference.value.filename}`
    }

    if (reference.relationTo === 'careers') {
      return `/vacancy/${reference.value}`
    }

    return `/${reference.relationTo}/${reference.value.slug}`
  }

  return ''
}

export const CMSLink = React.forwardRef((link: CMSLinkType, ref) => {
  const {
    type,
    url,
    newTab,
    reference,
    label,
    showLabel = true,
    variant,
    inverted,
    size,
    children,
    className,

    onMouseEnter,
    onMouseLeave,

    // TODO: fix LinkedIn tracking
    // linkedInTracking,
    // linkedInConversionId,
  } = link
  let href = generateHref({ type, url, reference })

  if (!href) {
    return (
      <span className={className} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        {showLabel && label && label}
        {children}
      </span>
    )
  }

  if (!variant) {
    const hrefIsLocal = ['tel:', 'mailto:', '/'].some(prefix => href.startsWith(prefix))

    if (!hrefIsLocal) {
      try {
        const objectURL = new URL(href)
        if (objectURL.origin === process.env.NEXT_PUBLIC_HOST_URL) {
          href = objectURL.href.replace(process.env.NEXT_PUBLIC_HOST_URL, '')
        }
      } catch (e) {
        console.error(`Failed to format url: ${href}`, e) // eslint-disable-line no-console
      }
    }

    const newTabProps = newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}

    if (href.indexOf('/') === 0) {
      return (
        <Link
          href={href}
          {...newTabProps}
          className={className}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          prefetch={false}
        >
          {showLabel && label && label}
          {children && children}
        </Link>
      )
    }

    return (
      <a
        href={url}
        {...newTabProps}
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {showLabel && label && label}
        {children && children}
      </a>
    )
  }

  const buttonProps: ButtonProps = {
    size,
    newTab,
    variant,
    inverted,
    onMouseEnter,
    onMouseLeave,
    ...link.buttonProps,
  }

  if (variant === 'default') {
    buttonProps.icon = 'arrow'
  }

  if (variant === 'ghost') {
    buttonProps.size = 'none'
  }

  return (
    <Button {...buttonProps} variant={variant} asChild className={className}>
      <Link
        href={href}
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        prefetch={false}
      >
        {showLabel && label && label}
        {children && children}
      </Link>
    </Button>
  )

  // TODO: add scroll to element support
  // if (type === 'scroll') {
  //   return (
  //     <a
  //       className={className}
  //       onClick={() =>
  //         document
  //           .getElementById(elementId)!
  //           .scrollIntoView({ behavior: 'smooth', block: 'start' })
  //       }
  //     >
  //       {children}
  //     </a>
  //   );
  // }
})
